import React, { Component } from "react";
import PropTypes from "prop-types";
import * as API from "lib/endpoints/checklists";
import Header from "./components/Header";
import Table, {
  ChecklistTableAdmin as TableAdmin,
  ChecklistTablePortal as TablePortal,
} from "./components/Table";
import Item from "./components/Item";
import Reminder from "./components/Reminder";
import Copy from "./components/Copy";
import * as CONSTANTS from "./constants";
import { ItemTemplate } from "./templates";
import moment from "moment";
import { CircularProgress } from "Components/Base/Feedback/CircularProgress";

class Checklist extends Component {
  static Header = Header;
  static Table = Table;
  static TablePortal = TablePortal;
  static TableAdmin = TableAdmin;
  static Item = Item;
  static Reminder = Reminder;
  static Copy = Copy;

  state = {
    activityId: null,
    languageContext: {},
    checklist: [],
    activeItem: ItemTemplate,
    activeItemID: null,
    selectedItems: [],
    displayItem: false,
    displayReminder: false,
    displayCopy: false,
    ready: false,
  };

  checklistItems_actions = {
    onUpdateChecklistItem: this.updateChecklistItem.bind(this),
  };

  checklistReminder_actions = {
    onSendTestReminder: this.sendTestReminder.bind(this),
    onUpdateReminder: this.updateReminder.bind(this),
    onAddReminder: this.addReminder.bind(this),
    onToggleReceiver: this.toggleReceiver.bind(this),
  };

  checklist_actions = {
    onCancel: this.resetView.bind(this),
    onNewItem: this.initNewItem.bind(this),
    onCopy: this.displayCopy.bind(this),
    onCopyChecklist: this.copyChecklist.bind(this),
    onEditItem: this.editItem.bind(this),
    onAddItem: this.addItem.bind(this),
    onUpdateItem: this.updateItem.bind(this),
    onSelectItem: this.selectItem.bind(this),
    onCompleteItem: this.completeItem.bind(this),
    onDeleteItem: this.deleteItem.bind(this),
    onDeleteItems: this.deleteItems.bind(this),
    onCloneItem: this.cloneItem.bind(this),
    onReset: this.resetView.bind(this),
    onRemind: this.displayReminder.bind(this),
    onValidation: this.validateItemData.bind(this),
    checklistItems_actions: this.checklistItems_actions,
    checklistReminder_actions: this.checklistReminder_actions,
  };

  componentDidMount() {
    const { id, languageContext } = this.props;

    this.setState({
      languageContext,
      activityId: id,
    });
    if (id) {
      this.fetchChecklist(id);
    }
  }

  async fetchChecklist(activityId) {
    let checklist = await API.getActivityChecklist(activityId);
    let activitiesToCopyFrom = await API.getChecklistsForCopying(activityId);

    if (checklist !== CONSTANTS.ERROR_MESSAGE) {
      this.setState({
        checklist: this.sortChecklist(checklist),
        activitiesToCopyFrom,
        displayItem: false,
        activeItemID: null,
        ready: true,
      });
    } else {
      this.props.onNotification(
        `${this.props.languageContext("error")}`,
        "error",
      );
    }
  }

  async fetchActivitiesToCopyFrom(activityId) {
    let activitiesToCopyFrom = await API.getChecklistsForCopying(activityId);

    if (activitiesToCopyFrom !== CONSTANTS.ERROR_MESSAGE) {
      this.setState({ activitiesToCopyFrom, ready: true });
    } else {
      this.props.onNotification(
        `${this.props.languageContext("error")}`,
        "error",
      );
    }
  }

  async fetchReminder(checklistItemID) {
    let itemReminder = await API.getReminder(checklistItemID);
    return itemReminder;
  }

  async fetchItem(itemId) {
    let item = this.state.checklist.tasks.find(
      (Item) => Item.checklistItemID === itemId,
    );

    let updatedItem = Object.assign({}, item);
    let itemReminder = await this.fetchReminder(item.checklistItemID);
    updatedItem.reminder = itemReminder;

    return updatedItem;
  }

  sortChecklist(checklist) {
    let sortedChecklist = Object.assign({}, checklist);
    sortedChecklist.tasks.forEach((a) =>
      a.deadline === null
        ? (a.deadline = CONSTANTS.ITEM_DEADLINE_BLANK)
        : a.deadline,
    );

    let sortedTasks = sortedChecklist.tasks.sort((a, b) =>
      a.deadline > b.deadline
        ? 1
        : a.deadline < b.deadline || a.deadline === null
          ? -1
          : 0,
    );
    sortedChecklist.tasks = sortedTasks;

    return sortedChecklist;
  }

  initNewItem() {
    let newItem = Object.assign({}, ItemTemplate);
    newItem.checklistID = this.state.checklist.id;
    newItem.reminder.receivers = this.state.checklist.receivers;

    this.setState({
      activeItemID: null,
      activeItem: newItem,
      displayItem: true,
    });
  }

  resetView() {
    this.setState({
      displayItem: false,
      displayReminder: false,
      displayCopy: false,
    });
  }

  validEmail(email) {
    let re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  validateItemData(item) {
    let validation = {
      itemtitle_valid: item.title !== null && item.title.length > 0,
      itemdeadline_valid:
        item.deadline == "none" || !item.deadline
          ? true
          : moment(item.deadline).isValid(),
      reminderDateIsBeforeDeadline:
        item.deadline != "none" && item.deadline
          ? moment(item.reminder.reminderDate).isSameOrBefore(
              item.deadline,
              "day",
            )
          : true,
      reminderDateIsAfterToday: moment(item.reminder.reminderDate).isAfter(
        new Date(),
        "day",
      ),
      reminderDateFormat_valid: moment(item.reminder.reminderDate).isValid(),
      reminderDate_valid:
        item.reminder &&
        ((parseInt(item.reminder.reminderType) ===
          CONSTANTS.REMINDER_TYPE_DATE &&
          moment(item.reminder.reminderDate).isValid() &&
          (item.deadline == "none" ||
            !item.deadline ||
            moment(item.reminder.reminderDate).isSameOrBefore(
              item.deadline,
              "day",
            )) &&
          moment(item.reminder.reminderDate).isAfter(new Date(), "day")) ||
          parseInt(item.reminder.reminderType) ===
            CONSTANTS.REMINDER_TYPE_NONE ||
          parseInt(item.reminder.reminderType) ===
            CONSTANTS.REMINDER_TYPE_RELATIVE),
      reminderEmailAddresses_valid:
        (item.reminder && item.reminder.emailAddresses === null) ||
        item.reminder.emailAddresses === "" ||
        (item.reminder &&
          item.reminder.emailAddresses.split(",").every(this.validEmail)),
    };

    let validated =
      validation.itemtitle_valid &&
      validation.itemdeadline_valid &&
      validation.reminderDate_valid &&
      validation.reminderEmailAddresses_valid;

    validation.validated = validated;

    return validation;
  }

  async editItem(itemId) {
    let activeItem = await this.fetchItem(itemId);
    let copiedItem = Object.assign({}, activeItem);
    copiedItem.reminder.checklistItemId = activeItem.checklistItemID;

    this.setState({ activeItem, displayItem: true });
  }

  displayReminder(itemId) {
    this.setState({ activeItemID: itemId, displayReminder: true });
  }

  displayCopy(itemId) {
    this.setState({ activeItemID: itemId, displayCopy: true });
  }

  async addItem(item) {
    const { onNotification, languageContext } = this.props;

    if (!item) {
      return null;
    }

    let ItemValidation = this.validateItemData(item);

    if (!ItemValidation.validated) {
      onNotification(`${languageContext("invalidform")}`, "error");
    } else {
      let newItem = Object.assign({}, item);
      newItem.checklistID = this.state.checklist.id;
      newItem.reminder.checklistId = this.state.checklist.id;
      let addedItem = await API.addChecklistItem(
        newItem,
        this.state.activityId,
        2,
      );
      if (addedItem !== CONSTANTS.ERROR_MESSAGE) {
        let newReminder = Object.assign({}, newItem.reminder);
        newReminder.checklistItemId = addedItem.checklistItemID;

        this.checklistReminder_actions.onAddReminder(newReminder);

        this.fetchChecklist(this.state.activityId);
        onNotification(`${languageContext("taskwascreated")} ${newItem.title}`);
      } else {
        onNotification(`${languageContext("error")}`, "error");
      }
    }
  }

  async updateItem(updatedItem) {
    const { onNotification, languageContext } = this.props;

    if (!updatedItem) {
      return null;
    }

    let ItemValidation = this.validateItemData(updatedItem);

    if (!ItemValidation.validated) {
      onNotification(`${languageContext("invalidform")}`, "error");
    } else {
      let response = await API.updateChecklistItem(updatedItem);
      if (response !== CONSTANTS.ERROR_MESSAGE) {
        if (updatedItem.reminder.id > 0) {
          this.checklistReminder_actions.onUpdateReminder(updatedItem.reminder);
        } else {
          this.checklistReminder_actions.onAddReminder(updatedItem.reminder);
        }

        this.fetchChecklist(this.state.activityId);
        onNotification(
          `${languageContext("taskwasupdated")} ${updatedItem.title}`,
        );
      } else {
        onNotification(`${languageContext("error")}`, "error");
      }
    }
  }

  async deleteItem(ItemId) {
    const { onNotification, languageContext } = this.props;

    let confirmed = confirm(languageContext("confirmdeletetask"));
    if (confirmed && ItemId) {
      let response = await API.deleteChecklistItem(ItemId);
      if (response !== CONSTANTS.ERROR_MESSAGE) {
        let checklist = this.state.checklist;
        let updatedTasks = this.state.checklist.tasks.filter(
          (Item) => Item.checklistItemID !== ItemId,
        );
        let updatedChecklist = Object.assign({}, checklist);
        updatedChecklist.tasks = updatedTasks;
        this.setState({ checklist: updatedChecklist });
        onNotification(`${languageContext("taskwasdeleted")}`);
      } else {
        onNotification(`${languageContext("error")}`, "error");
      }
    }
  }

  async deleteItems() {
    const { onNotification, languageContext } = this.props;
    if (this.state.selectedItems.length > 0) {
      let confirmed = confirm(languageContext("confirmdeletetasks"));
      if (confirmed && this.state.selectedItems) {
        let response = await API.deleteChecklistItems(
          this.state.selectedItems.join(", "),
        );
        let itemIds = this.state.selectedItems.join(", ");
        if (response !== CONSTANTS.ERROR_MESSAGE) {
          let checklist = this.state.checklist;

          let updatedChecklist = Object.assign({}, checklist);
          let updatedTasks = this.state.checklist.tasks.filter(
            (Item) =>
              this.state.selectedItems.indexOf(Item.checklistItemID) == -1,
          );
          updatedChecklist.tasks = updatedTasks;

          this.setState({ checklist: updatedChecklist, selectedItems: [] });
          onNotification(`${languageContext("tasksdeleted")}`);
        } else {
          onNotification(`${languageContext("error")}`, "error");
        }
      }
    } else {
      alert(languageContext("notasksselected"));
    }
  }

  selectItem(checked, item) {
    let selectedItems = this.state.selectedItems;
    if (checked) {
      selectedItems.push(item.checklistItemID);
    } else {
      let ix = selectedItems.findIndex((i) => i == item.checklistItemID);

      if (ix !== -1) selectedItems.splice(ix, 1);
    }
    this.setState({ selectedItems });
  }

  async completeItem(checked, item) {
    await API.setChecklistItemStatus(item.checklistItemID, checked);
    this.fetchChecklist(this.state.activityId);
  }

  async cloneItem(ItemId) {
    const { onNotification, languageContext } = this.props;
    if (!ItemId) {
      return null;
    }
    let newItem = {
      ...this.state.checklist.tasks.find(
        (Item) => Item.checklistItemID === ItemId,
      ),
    };
    newItem.title += `_${"copy"}`;
    if (!newItem.checklistItemID) {
      return null;
    }
    delete newItem.checklistItemID;
    let copiedItem = await API.addChecklistItem(
      newItem,
      this.state.activityId,
      2,
    );
    if (copiedItem !== CONSTANTS.ERROR_MESSAGE) {
      let reminder = await this.fetchReminder(ItemId);

      let newReminder = Object.assign({}, reminder);
      newReminder.checklistItemId = copiedItem.checklistItemID;

      this.checklistReminder_actions.onAddReminder(newReminder);
      this.fetchChecklist(this.state.activityId);
      onNotification(`${languageContext("taskwascopied")} ${newItem.title}`);
    } else {
      onNotification(`${languageContext("error")}`, "error");
    }
  }

  updateChecklistItem(updatedItem) {
    this.setState({ activeItem: updatedItem });
  }

  async copyChecklist(checklistId, overwrite) {
    await API.copyChecklist(this.state.activityId, 2, checklistId, overwrite);
    this.fetchChecklist(this.state.activityId);
    this.setState({ displayCopy: false });
  }

  async addReminder(reminder) {
    if (reminder) {
      let addedReminder = await API.addReminder(reminder);
      return addedReminder;
    }
    return null;
  }

  async updateReminder(reminder) {
    if (reminder) {
      let updatedReminder = await API.updateReminder(reminder);
      return updatedReminder;
    }
    return null;
  }

  async sendTestReminder(title, description, deadline, reminder) {
    let checklistItem = {};
    checklistItem.checklistItemId = reminder.checklistItemId;
    checklistItem.title = title;
    checklistItem.description = description;
    checklistItem.deadline = deadline;
    checklistItem.reminder = reminder;

    let response = await API.sendTestReminder(
      checklistItem,
      this.state.activityId,
    );

    if (response == 0) {
      this.props.onNotification(
        `${this.props.languageContext("testmessagewassent")}`,
      );
    } else {
      this.props.onNotification(
        `${this.props.languageContext("sendtestmessageerror")}`,
      );
    }
  }

  toggleReceiver(reminder, receiver, checked) {
    let item = this.state.activeItem;
    let updatedItem = Object.assign({}, item);
    let receiverIndex = reminder.receivers.findIndex(
      (Receiver) => Receiver.personalId === receiver.personalId,
    );
    let newReceiver = reminder.receivers.find(
      (Receiver) => Receiver.personalId === receiver.personalId,
    );
    // Need to create deep copy since newReceiver is read only
    let deepCopiedNewReciever = JSON.parse(JSON.stringify(newReceiver));

    deepCopiedNewReciever.selected = checked;
    updatedItem.reminder.receivers[receiverIndex] = deepCopiedNewReciever;

    this.setState({ activeItem: updatedItem });
  }

  render() {
    const { state, checklist_actions } = this;
    const circularLoaderStyle = { padding: "20px" };

    return state.ready ? (
      <div {...(this.props.className && { className: this.props.className })}>
        {this.props.children(state, checklist_actions)}
      </div>
    ) : (
      <div style={circularLoaderStyle}>
        <CircularProgress size={32} />
      </div>
    );
  }
}

Checklist.propTypes = {
  languageContext: PropTypes.func.isRequired,
  onNotification: PropTypes.func,
  children: PropTypes.node.isRequired,
};

Checklist.defaultProps = {
  languageContext: () => "string",
  children: [],
};

export default Checklist;
